import { useEffect, useRef, useCallback } from 'react';
import { cb } from '../widgetLibrary';

/**
 * Custom hook to simulate user activity or send a keep-alive signal periodically.
 *
 * @param {number} period - Interval in seconds between each keep-alive action.
 * @param {number} timeout - Total duration in seconds to keep the session alive (default: 4 hours).
 * @returns { stop: () => void } - A function to manually stop the keep-alive behavior.
 */

const DEFAULT_PERIOD = 5 * 60;
const DEFAULT_TIMEOUT = 4 * 60 * 60;

export function useKeepUserSessionAlive(
  period: number = DEFAULT_PERIOD,
  timeout: number = DEFAULT_TIMEOUT
): { stop: () => void } {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibilityStateOverridden = useRef(false);
  const timeSinceLoginInMinutesRef = useRef(0);

  const refreshState = useCallback(() => {
    // levity use document.hidden and visibilityState to detect user activity
    // https://github.com/collegeboard-software/ice-levity/blob/main/src/cb_iam.js#L364-L372
    // Override document.hidden to always report false
    if (!document) return;
    if (!isVisibilityStateOverridden.current) {
      Object.defineProperty(document, 'hidden', {
        configurable: true,
        get: function () {
          return false;
        },
      });
      // Also override visibilityState so that it reports 'visible'
      Object.defineProperty(document, 'visibilityState', {
        configurable: true,
        get: function () {
          return 'visible';
        },
      });

      isVisibilityStateOverridden.current = true;
    }

    // Levity use 5 events to track user activity
    // https://github.com/collegeboard-software/ice-levity/blob/84309f37e67d749ef5b1cfc3bc84f96d26470bb0/src/cb_iam.js#L236
    // Dispatch a click event
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    document.dispatchEvent(clickEvent);

    // Dispatch a visibilitychange event
    const visibilityChangeEvent = new Event('visibilitychange');
    document.dispatchEvent(visibilityChangeEvent);
  }, []);

  const clearState = useCallback(() => {
    if (!document) return;
    // eslint-disable-next-line
    delete (document as any).hidden;
    // eslint-disable-next-line
    delete (document as any).visibilityState;

    isVisibilityStateOverridden.current = false;

    document.dispatchEvent(new Event('visibilitychange'));
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    clearState();
  }, [clearState]);

  // eslint-disable-next-line
  const keepAlive = useCallback(() => {
    const authSession = cb?.core?.iam?.getAuthSession?.();
    if (authSession?.isLoggedIn && timeSinceLoginInMinutesRef.current < timeout / 60) {
      timeSinceLoginInMinutesRef.current = Math.floor((Date.now() - authSession.createTimeInMS) / 1000 / 60);

      refreshState();
    } else {
      timeSinceLoginInMinutesRef.current = Math.floor((Date.now() - (authSession?.createTimeInMS || 0)) / 1000 / 60);
      stop();
    }
    // eslint-disable-next-line
  }, [refreshState]);

  useEffect(() => {
    keepAlive();
    intervalRef.current = setInterval(keepAlive, period * 1000);

    // Cleanup on unmount
    return stop;
    // eslint-disable-next-line
  }, [period, timeout, stop, refreshState]);

  return { stop };
}
